import { getRuntimePartner } from '@/config/runtime';
import {
  REFERENCE_STORAGE_PREFIX,
  REFERENCE_TTL_MS,
} from '@/config/reference';
import { extractPartnerName } from '@/queries/currentPartnerQuery';

export interface IReferenceRecord {
  referenceId: string;
  capturedAt: number;
  /** Redundant with the storage key, but makes a logged record self-describing. */
  partner: string;
}

/**
 * Embedded deployments get the partner from window.__OSC_RUNTIME__, which the PHP page
 * inlines before this bundle loads; standalone ones derive it from the hostname. Either
 * source is synchronous and cannot change within a session, so capture and every later read
 * always agree on the key.
 */
export function currentPartnerSlug() {
  return (
    getRuntimePartner()?.slug || extractPartnerName(window.location.href) || '_'
  );
}

/**
 * Partner-scoped so a reference captured under one white-label can never be read under
 * another. Partner subdomains are already separate origins in production, so this is
 * belt-and-braces there — it holds in dev, where VITE_DEV_PARTNER forces a partner onto a
 * single origin.
 */
function referenceStorageKey() {
  return `${REFERENCE_STORAGE_PREFIX}:${currentPartnerSlug()}`;
}

export function clearReference() {
  try {
    localStorage.removeItem(referenceStorageKey());
  } catch {
    // A store that refuses writes never held it in the first place.
  }
}

/** Enforces the TTL here so no caller can forget it. */
export function readReference(): IReferenceRecord | null {
  try {
    const raw = localStorage.getItem(referenceStorageKey());
    if (!raw) return null;

    const parsed = JSON.parse(raw) as IReferenceRecord;
    if (!parsed?.referenceId) return null;

    if (Date.now() - parsed.capturedAt > REFERENCE_TTL_MS) {
      clearReference();
      return null;
    }

    return parsed;
  } catch {
    // Blocked storage, or JSON someone hand-edited in devtools.
    return null;
  }
}

/** The buffered reference id, or undefined when there is nothing to attribute. */
export function currentReferenceId(): string | undefined {
  return readReference()?.referenceId;
}

/**
 * Returns false when the write did not land. A blocked localStorage throws, but a full or
 * partially-blocked one can fail silently, so the value is read back rather than assumed.
 */
export function writeReference(record: IReferenceRecord) {
  try {
    localStorage.setItem(referenceStorageKey(), JSON.stringify(record));

    return readReference()?.referenceId === record.referenceId;
  } catch {
    return false;
  }
}
